import { useState, useEffect } from 'react';
import {
  Copy, Check, Sun, Moon, Monitor, Maximize2, Search, Plus, Trash2, Edit3,
  X, MessageCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { api, Widget, CreateWidgetRequest, Course, BubblePosition } from '../lib/api';

const WIDGET_URL = 'https://app.mycoursesearch.com/widget.js';

const MODE_OPTIONS = [
  { value: 'inline' as const, label: 'Inline', icon: Monitor, desc: 'Embedded search bar in your page' },
  { value: 'modal' as const, label: 'Modal', icon: Maximize2, desc: 'Opens in an overlay with Ctrl+K' },
  { value: 'bubble' as const, label: 'Bubble', icon: MessageCircle, desc: 'Floating button on your page' },
];

const BUBBLE_POSITIONS: { value: BubblePosition; label: string }[] = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

interface WidgetFormState {
  name: string;
  theme: 'light' | 'dark';
  accent_color: string;
  placeholder: string;
  results_limit: number;
  mode: 'inline' | 'modal' | 'bubble';
  shortcut: string;
  show_powered_by: boolean;
  bubble_position: BubblePosition;
  bubble_offset_x: number;
  bubble_offset_y: number;
  max_width: number;
  courseIds: string[];
}

const DEFAULT_FORM: WidgetFormState = {
  name: '',
  theme: 'light',
  accent_color: '#2563eb',
  placeholder: 'Search courses...',
  results_limit: 10,
  mode: 'inline',
  shortcut: 'k',
  show_powered_by: true,
  bubble_position: 'bottom-right',
  bubble_offset_x: 24,
  bubble_offset_y: 24,
  max_width: 600,
  courseIds: [],
};

export default function WidgetManager() {
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<WidgetFormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  // Embed code state
  const [expandedWidgetId, setExpandedWidgetId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([
        api.listWidgets(),
        api.getCourses(),
      ]);
      setWidgets(w);
      setCourses(c);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingWidget(null);
    setForm({ ...DEFAULT_FORM, name: `Widget ${widgets.length + 1}` });
    setShowForm(true);
  };

  const openEditForm = (widget: Widget) => {
    setEditingWidget(widget);
    setForm({
      name: widget.name,
      theme: widget.theme,
      accent_color: widget.accent_color,
      placeholder: widget.placeholder,
      results_limit: widget.results_limit,
      mode: widget.mode,
      shortcut: widget.shortcut,
      show_powered_by: widget.show_powered_by,
      bubble_position: widget.bubble_position,
      bubble_offset_x: widget.bubble_offset_x,
      bubble_offset_y: widget.bubble_offset_y,
      max_width: widget.max_width,
      courseIds: widget.courseIds,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingWidget(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Widget name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: CreateWidgetRequest = {
        name: form.name.trim(),
        theme: form.theme,
        accent_color: form.accent_color,
        placeholder: form.placeholder,
        results_limit: form.results_limit,
        mode: form.mode,
        shortcut: form.shortcut,
        show_powered_by: form.show_powered_by,
        bubble_position: form.bubble_position,
        bubble_offset_x: form.bubble_offset_x,
        bubble_offset_y: form.bubble_offset_y,
        max_width: form.max_width,
        courseIds: form.courseIds,
      };

      if (editingWidget) {
        await api.updateWidget(editingWidget.id, payload);
      } else {
        await api.createWidget(payload);
      }
      await loadData();
      closeForm();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this widget?')) return;
    try {
      await api.deleteWidget(id);
      setWidgets(prev => prev.filter(w => w.id !== id));
      if (expandedWidgetId === id) setExpandedWidgetId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const toggleCourse = (courseId: string) => {
    setForm(prev => ({
      ...prev,
      courseIds: prev.courseIds.includes(courseId)
        ? prev.courseIds.filter(id => id !== courseId)
        : [...prev.courseIds, courseId],
    }));
  };

  const generateEmbedCode = (widget: Widget) => {
    return `<script src="${WIDGET_URL}" data-widget-id="${widget.id}"></script>`;
  };

  const copyEmbed = (widgetId: string) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return;
    navigator.clipboard.writeText(generateEmbedCode(widget));
    setCopiedId(widgetId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Preview colors
  const getPreviewColors = (theme: 'light' | 'dark') =>
    theme === 'light'
      ? {
          bg: '#ffffff', border: '#e5e7eb', text: '#111827',
          textSecondary: '#6b7280', textMuted: '#9ca3af',
          inputBg: '#ffffff', resultBgHover: '#f3f4f6',
          pageBg: '#f9fafb',
        }
      : {
          bg: '#1f2937', border: '#374151', text: '#f9fafb',
          textSecondary: '#d1d5db', textMuted: '#9ca3af',
          inputBg: '#111827', resultBgHover: '#374151',
          pageBg: '#111827',
        };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Widgets</h2>
          <p className="text-sm text-gray-500 mt-1">Create and manage search widgets for your website</p>
        </div>
        <button
          onClick={openCreateForm}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> New Widget
        </button>
      </div>

      {/* Widget List */}
      {widgets.length === 0 && !showForm ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">No widgets yet</h3>
          <p className="text-sm text-gray-500 mb-4">Create your first widget to get an embed code for your website.</p>
          <button
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> Create Widget
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {widgets.map(widget => {
            const isExpanded = expandedWidgetId === widget.id;
            const colors = getPreviewColors(widget.theme);
            const modeInfo = MODE_OPTIONS.find(m => m.value === widget.mode);
            const ModeIcon = modeInfo?.icon || Monitor;
            const widgetCourses = courses.filter(c => widget.courseIds.includes(c.id));

            return (
              <div key={widget.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                {/* Widget header */}
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${widget.accent_color}15`, color: widget.accent_color }}
                    >
                      <ModeIcon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{widget.name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500 capitalize">{widget.mode}</span>
                        <span className="text-gray-300">|</span>
                        <span className="text-xs text-gray-500">{widget.theme} theme</span>
                        {widgetCourses.length > 0 && (
                          <>
                            <span className="text-gray-300">|</span>
                            <span className="text-xs text-gray-500">
                              {widgetCourses.length} course{widgetCourses.length !== 1 ? 's' : ''}
                            </span>
                          </>
                        )}
                        {widget.courseIds.length === 0 && (
                          <>
                            <span className="text-gray-300">|</span>
                            <span className="text-xs text-gray-500">All courses</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditForm(widget)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(widget.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setExpandedWidgetId(isExpanded ? null : widget.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={isExpanded ? 'Collapse' : 'Show embed code'}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded: Preview + Embed Code */}
                {isExpanded && (
                  <div className="border-t px-5 py-5 space-y-5">
                    {/* Mini preview */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
                      <div className="rounded-xl p-5 border" style={{ backgroundColor: colors.pageBg }}>
                        {widget.mode === 'bubble' ? (
                          <div className="flex items-end justify-end relative" style={{ minHeight: 120 }}>
                            <div
                              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg cursor-default"
                              style={{ backgroundColor: widget.accent_color, color: 'white' }}
                            >
                              <Search className="w-6 h-6" />
                            </div>
                          </div>
                        ) : widget.mode === 'modal' ? (
                          <div>
                            <div
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm cursor-default"
                              style={{
                                backgroundColor: colors.inputBg,
                                borderColor: colors.border,
                                color: colors.textMuted,
                              }}
                            >
                              <Search className="w-4 h-4" />
                              <span>{widget.placeholder}</span>
                              <kbd
                                className="ml-4 px-1.5 py-0.5 text-xs rounded"
                                style={{
                                  backgroundColor: colors.resultBgHover,
                                  border: `1px solid ${colors.border}`,
                                  color: colors.textMuted,
                                }}
                              >
                                Ctrl+{widget.shortcut.toUpperCase()}
                              </kbd>
                            </div>
                            <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                              Click or press shortcut to open search overlay
                            </p>
                          </div>
                        ) : (
                          <div className="relative" style={{ maxWidth: 500 }}>
                            <div className="relative">
                              <Search
                                className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5"
                                style={{ color: colors.textMuted }}
                              />
                              <div
                                className="w-full pl-11 pr-4 py-3 rounded-xl text-sm"
                                style={{
                                  backgroundColor: colors.inputBg,
                                  border: `2px solid ${widget.accent_color}`,
                                  boxShadow: `0 0 0 3px ${widget.accent_color}20`,
                                  color: colors.text,
                                }}
                              >
                                <span style={{ color: colors.textMuted }}>{widget.placeholder}</span>
                              </div>
                            </div>
                            <div
                              className="mt-2 rounded-xl border overflow-hidden"
                              style={{
                                backgroundColor: colors.bg,
                                borderColor: colors.border,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                              }}
                            >
                              {[
                                { title: 'Getting Started', course: 'Course A' },
                                { title: 'Advanced Topics', course: 'Course B' },
                              ].map((item, i) => (
                                <div
                                  key={i}
                                  className="px-3.5 py-2.5"
                                  style={{
                                    backgroundColor: i === 0 ? colors.resultBgHover : 'transparent',
                                    borderBottom: i === 0 ? `1px solid ${colors.border}` : undefined,
                                  }}
                                >
                                  <div className="font-medium text-xs" style={{ color: colors.text }}>
                                    {item.title}
                                  </div>
                                  <div className="mt-1">
                                    <span
                                      className="text-xs px-1.5 py-0.5 rounded"
                                      style={{
                                        backgroundColor: `${widget.accent_color}15`,
                                        color: widget.accent_color,
                                      }}
                                    >
                                      {item.course}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {widget.show_powered_by && (
                                <div
                                  className="text-center py-2 text-xs"
                                  style={{
                                    borderTop: `1px solid ${colors.border}`,
                                    color: colors.textMuted,
                                  }}
                                >
                                  Powered by: <span style={{ color: colors.textSecondary, fontWeight: 500 }}>My Course Search</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Embed Code */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Embed Code</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Paste this snippet into your website. Settings are loaded automatically from the server — no code changes needed when you update the widget.
                      </p>
                      <div className="relative">
                        <pre className="bg-gray-900 rounded-lg p-4 pr-20 overflow-x-auto">
                          <code className="text-green-400 text-sm whitespace-pre">{generateEmbedCode(widget)}</code>
                        </pre>
                        <button
                          onClick={() => copyEmbed(widget.id)}
                          className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-md transition-colors"
                        >
                          {copiedId === widget.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedId === widget.id ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-[5vh] overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 mb-8">
            {/* Form header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingWidget ? 'Edit Widget' : 'New Widget'}
              </h3>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="My Search Widget"
                />
              </div>

              {/* Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Display Mode</label>
                <div className="grid grid-cols-3 gap-3">
                  {MODE_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setForm({ ...form, mode: opt.value })}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-sm transition-colors ${
                          form.mode === opt.value
                            ? 'bg-blue-50 border-blue-300 text-blue-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{opt.label}</span>
                        <span className="text-xs text-gray-500 text-center">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Theme + Accent Color in a row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setForm({ ...form, theme: 'light' })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        form.theme === 'light'
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Sun className="w-4 h-4" /> Light
                    </button>
                    <button
                      onClick={() => setForm({ ...form, theme: 'dark' })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        form.theme === 'dark'
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Moon className="w-4 h-4" /> Dark
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.accent_color}
                      onChange={e => setForm({ ...form, accent_color: e.target.value })}
                      className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                    />
                    <input
                      type="text"
                      value={form.accent_color}
                      onChange={e => {
                        const v = e.target.value;
                        if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                          setForm({ ...form, accent_color: v });
                        }
                      }}
                      className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Placeholder + Results Limit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder Text</label>
                  <input
                    type="text"
                    value={form.placeholder}
                    onChange={e => setForm({ ...form, placeholder: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Results Limit</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={form.results_limit}
                    onChange={e => setForm({ ...form, results_limit: Math.min(50, Math.max(1, parseInt(e.target.value) || 1)) })}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <span className="text-xs text-gray-500 ml-2">1-50</span>
                </div>
              </div>

              {/* Max Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Width (px)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={200}
                    max={2000}
                    step={10}
                    value={form.max_width}
                    onChange={e => setForm({ ...form, max_width: parseInt(e.target.value) })}
                    className="flex-1"
                  />
                  <input
                    type="number"
                    min={200}
                    max={2000}
                    value={form.max_width}
                    onChange={e => setForm({ ...form, max_width: Math.min(2000, Math.max(200, parseInt(e.target.value) || 200)) })}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center"
                  />
                  <span className="text-xs text-gray-500">px</span>
                </div>
              </div>

              {/* Keyboard Shortcut (modal/bubble) */}
              {(form.mode === 'modal' || form.mode === 'bubble') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keyboard Shortcut</label>
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Ctrl</kbd>
                    <span className="text-gray-400">+</span>
                    <input
                      type="text"
                      value={form.shortcut}
                      onChange={e => {
                        const v = e.target.value.slice(-1);
                        if (/^[a-zA-Z]$/.test(v)) {
                          setForm({ ...form, shortcut: v.toLowerCase() });
                        }
                      }}
                      className="w-12 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center font-mono uppercase"
                      maxLength={1}
                    />
                  </div>
                </div>
              )}

              {/* Bubble Position (bubble mode only) */}
              {form.mode === 'bubble' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bubble Position</label>
                    <div className="grid grid-cols-2 gap-2">
                      {BUBBLE_POSITIONS.map(pos => (
                        <button
                          key={pos.value}
                          onClick={() => setForm({ ...form, bubble_position: pos.value })}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            form.bubble_position === pos.value
                              ? 'bg-blue-50 border-blue-300 text-blue-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Horizontal offset (px)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={500}
                        value={form.bubble_offset_x}
                        onChange={e => setForm({ ...form, bubble_offset_x: Math.min(500, Math.max(0, parseInt(e.target.value) || 0)) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Vertical offset (px)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={500}
                        value={form.bubble_offset_y}
                        onChange={e => setForm({ ...form, bubble_offset_y: Math.min(500, Math.max(0, parseInt(e.target.value) || 0)) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Distance from the {form.bubble_position.replace('-', ' ')} corner of the screen.
                  </p>
                </div>
              )}

              {/* Show Powered By */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Show "Powered by CourseSearch"</label>
                <button
                  onClick={() => setForm({ ...form, show_powered_by: !form.show_powered_by })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.show_powered_by ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form.show_powered_by ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Course Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Searchable Courses
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select which courses this widget can search. Leave empty to search all courses.
                </p>
                {courses.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No courses yet. Add courses in the Courses tab.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                    {courses.map(course => (
                      <label
                        key={course.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.courseIds.includes(course.id)}
                          onChange={() => toggleCourse(course.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">{course.title}</span>
                      </label>
                    ))}
                  </div>
                )}
                {form.courseIds.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1.5">
                    {form.courseIds.length} course{form.courseIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* Live Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                <div
                  className="rounded-xl p-5 border"
                  style={{ backgroundColor: getPreviewColors(form.theme).pageBg }}
                >
                  {form.mode === 'bubble' ? (
                    <div className="flex items-end justify-end relative" style={{ minHeight: 80 }}>
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: form.accent_color, color: 'white' }}
                      >
                        <Search className="w-6 h-6" />
                      </div>
                    </div>
                  ) : form.mode === 'modal' ? (
                    <div>
                      <div
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm"
                        style={{
                          backgroundColor: getPreviewColors(form.theme).inputBg,
                          borderColor: getPreviewColors(form.theme).border,
                          color: getPreviewColors(form.theme).textMuted,
                        }}
                      >
                        <Search className="w-4 h-4" />
                        <span>{form.placeholder}</span>
                        <kbd
                          className="ml-3 px-1.5 py-0.5 text-xs rounded"
                          style={{
                            backgroundColor: getPreviewColors(form.theme).resultBgHover,
                            border: `1px solid ${getPreviewColors(form.theme).border}`,
                            color: getPreviewColors(form.theme).textMuted,
                          }}
                        >
                          Ctrl+{form.shortcut.toUpperCase()}
                        </kbd>
                      </div>
                      <p className="text-xs mt-2" style={{ color: getPreviewColors(form.theme).textMuted }}>
                        Click or press shortcut to open search overlay
                      </p>
                    </div>
                  ) : (
                    <div className="relative" style={{ maxWidth: 400 }}>
                      <div className="relative">
                        <Search
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                          style={{ color: getPreviewColors(form.theme).textMuted }}
                        />
                        <div
                          className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm"
                          style={{
                            backgroundColor: getPreviewColors(form.theme).inputBg,
                            border: `2px solid ${form.accent_color}`,
                            boxShadow: `0 0 0 3px ${form.accent_color}20`,
                            color: getPreviewColors(form.theme).text,
                          }}
                        >
                          <span style={{ color: getPreviewColors(form.theme).textMuted }}>{form.placeholder}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Form footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={closeForm}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Saving...
                  </>
                ) : (
                  editingWidget ? 'Save Changes' : 'Create Widget'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
